import { CSSProperties, createElement, ReactNode, useMemo } from 'react';

import { Box } from '@mui/material';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';

import { Card, GeneralLoader, SectionErrorBoundary } from '~components';
import ErrorType from '~components/errorBoundary/errorType';
import ErrorView from '~components/errorBoundary/errorView';
import {
	findWikiPageByFile,
	findWikiPageById,
	isWikiPageAuthorized,
	type WikiManifest,
} from '~features/wiki/utils/wikiManifestAccess';
import { useSession } from '~hooks';
import Dimens from '~themes/dimens';

import wikiManifest from '../../docs/manifest.json';

const extractBodyHtml = (htmlDocument: string): string => {
	try {
		const doc = new DOMParser().parseFromString(htmlDocument, 'text/html');
		return doc.body?.innerHTML || htmlDocument;
	} catch {
		const match = htmlDocument.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
		return match?.[1] ?? htmlDocument;
	}
};

const isSafeUrl = (url: string): boolean => {
	const normalized = url.trim().toLowerCase();
	if (
		normalized.startsWith('/') ||
		normalized.startsWith('#') ||
		normalized.startsWith('mailto:') ||
		normalized.startsWith('tel:')
	) {
		return true;
	}

	if (/^[a-z][a-z\d+\-.]*:/.test(normalized)) {
		return normalized.startsWith('http://') || normalized.startsWith('https://');
	}

	// Relative URLs such as ../file.html or ./file.html are also safe.
	return true;
};

const parseStyleAttribute = (style: string): CSSProperties => {
	const parsed: CSSProperties = {};
	const declarations = style.split(';');

	for (const declaration of declarations) {
		const firstColon = declaration.indexOf(':');
		if (firstColon < 0) {
			continue;
		}

		const property = declaration.slice(0, firstColon).trim();
		const value = declaration
			.slice(firstColon + 1)
			.trim()
			.replace(/!important/i, '')
			.trim();
		if (!property || !value) {
			continue;
		}

		const reactProperty = property
			.toLowerCase()
			.replace(/-([a-z])/g, (_match, char: string) => char.toUpperCase());
		(parsed as Record<string, string>)[reactProperty] = value;
	}

	return parsed;
};

const mapHtmlAttributesToReactProps = (element: Element): Record<string, unknown> => {
	const props: Record<string, unknown> = {};

	for (const attribute of Array.from(element.attributes)) {
		const name = attribute.name;
		const value = attribute.value;

		if (name.startsWith('on')) {
			continue;
		}

		if (name === 'class') {
			props.className = value;
			continue;
		}

		if (name === 'for') {
			props.htmlFor = value;
			continue;
		}

		if (name === 'style') {
			props.style = parseStyleAttribute(value);
			continue;
		}

		if ((name === 'href' || name === 'src') && !isSafeUrl(value)) {
			continue;
		}

		props[name] = value;
	}

	if (element.tagName.toLowerCase() === 'a' && props.target === '_blank') {
		props.rel = 'noopener noreferrer';
	}

	return props;
};

const mapNodeToReact = (node: ChildNode, key: string): ReactNode => {
	if (node.nodeType === Node.TEXT_NODE) {
		return node.textContent;
	}

	if (node.nodeType !== Node.ELEMENT_NODE) {
		return null;
	}

	const element = node as Element;
	const tagName = element.tagName.toLowerCase();
	if (tagName === 'script' || tagName === 'style') {
		return null;
	}

	const props = mapHtmlAttributesToReactProps(element);
	props.key = key;
	const children = Array.from(element.childNodes).map((childNode, index) =>
		mapNodeToReact(childNode, `${key}-${index}`)
	);

	return createElement(tagName, props, ...children);
};

const renderHtmlToReact = (html: string): ReactNode => {
	const doc = new DOMParser().parseFromString(html, 'text/html');
	return Array.from(doc.body.childNodes).map((node, index) => mapNodeToReact(node, `${index}`));
};

const htmlByPath = import.meta.glob('/src/docs/**/*.html', {
	query: '?raw',
	import: 'default',
});

type ResolvedWikiTarget =
	| { kind: 'notFound' }
	| { kind: 'notAuthorized' }
	| { kind: 'page'; file: string; title?: string };

const WikiPage = () => {
	const { id } = useParams();
	const { user } = useSession();

	const resolvedTarget = useMemo<ResolvedWikiTarget>(() => {
		const manifest = wikiManifest as unknown as WikiManifest;

		if (id) {
			const page = findWikiPageById(manifest.toc, id);
			if (!page) {
				return { kind: 'notFound' };
			}
			if (!isWikiPageAuthorized(page, user?.role)) {
				return { kind: 'notAuthorized' };
			}
			return { kind: 'page', file: page.file, title: page.title };
		}

		if (!manifest.indexPath) {
			return { kind: 'notFound' };
		}

		// Resolve indexPath to the same mechanism as other pages (id -> file) when possible.
		// If the index file is not present in the ToC pages, we still render it directly.
		const tocIndexPage = findWikiPageByFile(manifest.toc, manifest.indexPath);
		if (tocIndexPage && !isWikiPageAuthorized(tocIndexPage, user?.role)) {
			return { kind: 'notAuthorized' };
		}
		return { kind: 'page', file: manifest.indexPath, title: tocIndexPage?.title };
	}, [id, user?.role]);

	const { data: rawHtml, error: rawHtmlError } = useSWR(
		resolvedTarget.kind === 'page' ? resolvedTarget.file : null,
		async (file: string) => {
			const moduleKey = `/src/docs/${file}`;
			const loader = htmlByPath[moduleKey];
			if (!loader) {
				throw new Error('Wiki page not found');
			}
			return (await loader()) as string;
		}
	);
	const isNotFound = resolvedTarget.kind === 'notFound' || rawHtmlError != null;

	const bodyHtml = useMemo(() => (rawHtml ? extractBodyHtml(rawHtml) : ''), [rawHtml]);
	const renderedWikiContent = useMemo(() => renderHtmlToReact(bodyHtml), [bodyHtml]);

	if (resolvedTarget.kind === 'notAuthorized') {
		return <ErrorView type={ErrorType.NotAuthorized} />;
	}

	if (resolvedTarget.kind === 'notFound' || isNotFound) {
		return <ErrorView type={ErrorType.NotFound} />;
	}

	return (
		<Card>
			<SectionErrorBoundary>
				{rawHtml ?
					<Box
						className='wiki-content'
						sx={(theme) => ({
							color: theme.palette.textPrimary.main,

							// Typography
							'& h1': {
								...theme.typography.headingLg,
								color: theme.palette.textPrimary.main,
								marginTop: 0,
								marginBottom: Dimens.spacingXl,
							},
							'& h2': {
								...theme.typography.headingSm,
								color: theme.palette.textPrimary.main,
								marginTop: Dimens.spacing3xl,
								marginBottom: Dimens.spacingLg,
							},
							'& h3': {
								...theme.typography.headingXs,
								color: theme.palette.textPrimary.main,
								marginTop: Dimens.spacing2xl,
								marginBottom: Dimens.spacingMd,
							},
							'& h4': {
								...theme.typography.labelLg,
								color: theme.palette.textPrimary.main,
								marginTop: Dimens.spacing2xl,
								marginBottom: Dimens.spacingMd,
							},
							'& h5, & h6': {
								...theme.typography.labelSm,
								color: theme.palette.textPrimary.main,
								marginTop: Dimens.spacingXl,
								marginBottom: Dimens.spacingMd,
							},
							'& p': {
								...theme.typography.paragraph,
								color: theme.palette.textPrimary.main,
								marginTop: Dimens.spacingNone,
								marginBottom: Dimens.spacingXl,
							},
							'& li': {
								...theme.typography.paragraph,
								color: theme.palette.textPrimary.main,
							},
							'& small': {
								...theme.typography.paragraphXs,
								color: theme.palette.textSecondary.main,
							},

							// Lists
							'& ul, & ol': {
								marginTop: Dimens.spacingNone,
								marginBottom: Dimens.spacingXl,
								paddingLeft: Dimens.spacing4xl,
							},
							'& li > ul, & li > ol': {
								marginTop: Dimens.spacingMd,
								marginBottom: Dimens.spacingMd,
							},

							// Links
							'& a': {
								color: theme.palette.textSecondary.main,
								textDecorationColor: theme.palette.textSecondary.main,
								textUnderlineOffset: '2px',
							},
							'& a:hover': {
								textDecoration: 'underline',
							},

							// Inline code
							'& code': {
								fontFamily:
									'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
								fontSize: '0.875em',
								backgroundColor: theme.palette.bgSecondary.main,
								border: `1px solid ${theme.palette.borderSecondary.main}`,
								borderRadius: Dimens.radiusSm,
								padding: `${Dimens.spacing3xs} ${Dimens.spacingSm}`,
							},

							// Code block figure wrapper (added by rehype-pretty-code)
							'& figure': {
								margin: 0,
							},

							// Code blocks
							'& pre': {
								marginTop: Dimens.spacingNone,
								marginBottom: Dimens.spacingXl,
								backgroundColor: theme.palette.bgSecondary.main,
								border: `1px solid ${theme.palette.borderSecondary.main}`,
								borderRadius: Dimens.radiusMd,
								padding: `${Dimens.spacingSm} ${Dimens.spacingLg}`,
								overflowX: 'auto',
								WebkitOverflowScrolling: 'touch',
							},
							'& pre code': {
								backgroundColor: 'transparent',
								border: 'none',
								padding: 0,
								borderRadius: 0,
								fontSize: '0.9em',
							},

							// Blockquotes
							'& blockquote': {
								margin: `0 0 ${Dimens.spacingXl} 0`,
								padding: `${Dimens.spacingSm} ${Dimens.spacingLg}`,
								backgroundColor: theme.palette.bgSecondary.main,
								borderLeft: `4px solid ${theme.palette.borderBrandPrimary.main}`,
								borderRadius: Dimens.radiusMd,
								color: theme.palette.textSecondary.main,
							},
							'& blockquote p': {
								...theme.typography.paragraph,
								color: theme.palette.textSecondary.main,
								marginBottom: Dimens.spacingNone,
							},

							// Tables
							'& table': {
								width: '100%',
								borderCollapse: 'collapse',
								display: 'block',
								overflowX: 'auto',
								marginTop: Dimens.spacingNone,
								marginBottom: Dimens.spacingXl,
							},
							'& th, & td': {
								...theme.typography.paragraphSm,
								border: `1px solid ${theme.palette.borderSecondary.main}`,
								padding: Dimens.spacingMd,
								textAlign: 'left',
								verticalAlign: 'top',
							},
							'& thead th': {
								...theme.typography.labelSm,
								backgroundColor: theme.palette.bgSecondary.main,
								color: theme.palette.textPrimary.main,
							},
							'& tbody tr:nth-of-type(even) td': {
								backgroundColor: theme.palette.bgPrimaryHover.main,
							},

							// Misc
							'& hr': {
								border: 0,
								borderTop: `1px solid ${theme.palette.borderSecondary.main}`,
								marginTop: Dimens.spacing2xl,
								marginBottom: Dimens.spacing2xl,
							},
							'& img': {
								maxWidth: '100%',
								height: 'auto',
							},
						})}
					>
						{renderedWikiContent}
					</Box>
				:	<GeneralLoader />}
			</SectionErrorBoundary>
		</Card>
	);
};

export default WikiPage;
